<script>
    import { createEventDispatcher, onMount } from "svelte";
    import {
        format as formatDate,
        isToday,
        isYesterday,
        isTomorrow,
    } from "date-fns";

    export let documents = [];
    export let currentDocumentId = null;

    const dispatch = createEventDispatcher();

    // Extract date from document title
    function extractDateFromTitle(title) {
        const dateMatch = title.match(/Journal (\d{4}-\d{2}-\d{2})/);
        if (dateMatch) {
            try {
                return new Date(dateMatch[1]);
            } catch (e) {
                return new Date();
            }
        }
        return new Date();
    }

    // Sort documents by date (newest first)
    $: sortedDocuments = [...documents].sort((a, b) => {
        const dateA = extractDateFromTitle(a.title);
        const dateB = extractDateFromTitle(b.title);
        return dateB - dateA; // Sort newest to oldest
    });

    // Current document index in the sorted array
    $: currentIndex = currentDocumentId
        ? sortedDocuments.findIndex((doc) => doc.id === currentDocumentId)
        : -1;

    // Determine if we have previous/next documents
    $: hasPrevious =
        currentIndex !== -1 && currentIndex < sortedDocuments.length - 1;
    $: hasNext = currentIndex > 0;

    // Get the current document object
    $: currentDocument = currentDocumentId
        ? documents.find((doc) => doc.id === currentDocumentId)
        : null;

    // Get the current date from document title
    $: currentDate = currentDocument
        ? extractDateFromTitle(currentDocument.title)
        : new Date();

    // Format date for display with relative terms
    function formatDisplayDate(date) {
        if (isToday(date)) return "Today";
        if (isYesterday(date)) return "Yesterday";
        if (isTomorrow(date)) return "Tomorrow";

        return formatDate(date, "EEEE, MMMM d, yyyy");
    }

    // Navigate to previous document (older)
    function goToPrevious() {
        if (hasPrevious) {
            const prevDoc = sortedDocuments[currentIndex + 1];
            dispatch("switchDocument", prevDoc.id);
        }
    }

    // Navigate to next document (newer)
    function goToNext() {
        if (hasNext) {
            const nextDoc = sortedDocuments[currentIndex - 1];
            dispatch("switchDocument", nextDoc.id);
        }
    }

    // Function to check if today's document exists
    function getTodayDocument() {
        const today = new Date();
        const todayDateString = formatDate(today, "yyyy-MM-dd");
        const exactTitle = `Journal ${todayDateString}`;
        return documents.find((doc) => doc.title === exactTitle);
    }

    onMount(() => {
        // Check if today's document exists, if not create it
        const todayDoc = getTodayDocument();
        if (!todayDoc && documents.length > 0) {
            dispatch("createDocument");
        }
    });
</script>

<div class="document-nav">
    <div class="nav-controls">
        <!-- Left arrow (older) -->
        <button
            class="nav-arrow"
            on:click={goToPrevious}
            disabled={!hasPrevious}
            aria-label="Previous entry"
        >
            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
            >
                <path d="M15 18l-6-6 6-6"></path>
            </svg>
        </button>

        <!-- Date display -->
        <div class="date-display">
            {formatDisplayDate(currentDate)}
        </div>

        <!-- Right arrow (newer) -->
        <button
            class="nav-arrow"
            on:click={goToNext}
            disabled={!hasNext}
            aria-label="Next entry"
        >
            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
            >
                <path d="M9 18l6-6-6-6"></path>
            </svg>
        </button>
    </div>
</div>

<style>
    .document-nav {
        max-width: 800px;
        margin: 0 auto 24px auto;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 16px 0;
        border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    }

    .nav-controls {
        display: flex;
        align-items: center;
        gap: 16px;
    }

    .date-display {
        font-size: 16px;
        color: #374151;
        font-weight: 500;
        min-width: 220px;
        text-align: center;
    }

    .nav-arrow {
        background: transparent;
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #374151;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        transition: background-color 0.2s;
    }

    .nav-arrow:hover:not(:disabled) {
        background-color: rgba(0, 0, 0, 0.05);
    }

    .nav-arrow:disabled {
        color: #d1d5db;
        cursor: not-allowed;
    }
</style>
